import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../config/firebase';
import { collection, doc, setDoc, Timestamp } from 'firebase/firestore';

interface CredentialingFormData {
  npi: string;
  driversLicense: string;
  malpracticeInsurance: string;
  w9: File | null;
  hasMedicaidNumber: boolean;
  medicaidNumber: string;
  state: string;
  insurancePanels: string[];
}

const CredentialingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<CredentialingFormData>({
    npi: '',
    driversLicense: '',
    malpracticeInsurance: '',
    w9: null,
    hasMedicaidNumber: false,
    medicaidNumber: '',
    state: '',
    insurancePanels: []
  });

  const states = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming'
  ];

  const insuranceOptions = [
    { category: 'Medicaid', options: ['Medicaid'] },
    {
      category: 'Care Management Organizations',
      options: ['CareSource', 'Amerigroup', 'Peachstate']
    },
    {
      category: 'Private Insurance',
      options: ['BCBS', 'Aetna', 'United Health', 'Optum']
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: checkbox.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({
      ...prev,
      w9: file
    }));
  };

  const handleInsurancePanelChange = (panel: string) => {
    setFormData(prev => ({
      ...prev,
      insurancePanels: prev.insurancePanels.includes(panel)
        ? prev.insurancePanels.filter(p => p !== panel)
        : [...prev.insurancePanels, panel]
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.npi.trim()) return 'NPI number is required';
    if (!/^\d{10}$/.test(formData.npi.trim())) return 'NPI must be a 10-digit number';
    if (!formData.driversLicense.trim()) return "Driver's license number is required";
    if (!formData.malpracticeInsurance.trim()) return 'Malpractice insurance policy number is required';
    if (!formData.state) return 'Practice state is required';
    if (formData.hasMedicaidNumber && !formData.medicaidNumber.trim()) return 'Medicaid number is required when checkbox is selected';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!user) {
      setError('You must be logged in to submit credentialing information.');
      return;
    }

    setIsLoading(true);

    try {
      const credentialDoc = {
        providerId: user.uid,
        npi: formData.npi.trim(),
        driversLicense: formData.driversLicense.trim(),
        malpracticeInsurance: formData.malpracticeInsurance.trim(),
        hasMedicaidNumber: formData.hasMedicaidNumber,
        medicaidNumber: formData.hasMedicaidNumber ? formData.medicaidNumber.trim() : '',
        state: formData.state,
        insurancePanels: formData.insurancePanels,
        status: 'pending_review',
        submittedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = doc(collection(db, 'provider_credentials'), user.uid);
      await setDoc(docRef, credentialDoc);

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit credentialing information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app">
      <Header showSessionStatus={false} sessionActive={false} />

      <main id="main-content" className="page">
        <div className="container" style={{ paddingTop: '2rem', maxWidth: '800px' }}>
          <div className="text-center" style={{ marginBottom: '3rem' }}>
            <h1 style={{
              fontSize: '2.5rem',
              marginBottom: '1rem',
              color: '#D4A017'
            }}>
              Credentialing Information
            </h1>
            <p style={{
              fontSize: '1.1rem',
              color: '#d1d5db',
              marginBottom: '2rem'
            }}>
              Please provide the following information to begin the credentialing process
            </p>
          </div>

          {success ? (
            <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>&#10003;</div>
              <h2 style={{ color: '#D4A017', marginBottom: '1rem' }}>Credentialing Submitted</h2>
              <p style={{ color: '#d1d5db', marginBottom: '2rem' }}>
                Your credentialing information has been submitted for review. You will be notified once it has been processed.
              </p>
              <button
                onClick={() => navigate('/provider-dashboard')}
                className="btn btn-primary"
              >
                Go to Dashboard
              </button>
            </div>
          ) : (
          <div className="card">
            <form onSubmit={handleSubmit} style={{ padding: '2rem' }}>
              {error && (
                <div className="form-error" role="alert" aria-live="assertive" style={{ marginBottom: '1.5rem', padding: '1rem', borderRadius: '0.5rem' }}>
                  {error}
                </div>
              )}

              {/* Required Documents Section */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{
                  fontSize: '1.5rem',
                  color: '#D4A017',
                  marginBottom: '1.5rem',
                  borderBottom: '2px solid #D4A017',
                  paddingBottom: '0.5rem'
                }}>
                  Required Documents & Information
                </h3>

                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  <div>
                    <label htmlFor="cred-npi" style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: '#f9fafb'
                    }}>
                      National Provider Identifier (NPI) *
                    </label>
                    <input
                      id="cred-npi"
                      type="text"
                      name="npi"
                      value={formData.npi}
                      onChange={handleInputChange}
                      required
                      aria-required="true"
                      placeholder="Enter your NPI number"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #4b5563',
                        background: '#374151',
                        color: '#f9fafb',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label htmlFor="cred-drivers-license" style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: '#f9fafb'
                    }}>
                      Driver's License Number *
                    </label>
                    <input
                      id="cred-drivers-license"
                      type="text"
                      name="driversLicense"
                      value={formData.driversLicense}
                      onChange={handleInputChange}
                      required
                      aria-required="true"
                      placeholder="Enter your driver's license number"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #4b5563',
                        background: '#374151',
                        color: '#f9fafb',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label htmlFor="cred-malpractice" style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: '#f9fafb'
                    }}>
                      Malpractice Insurance Policy Number *
                    </label>
                    <input
                      id="cred-malpractice"
                      type="text"
                      name="malpracticeInsurance"
                      value={formData.malpracticeInsurance}
                      onChange={handleInputChange}
                      required
                      aria-required="true"
                      placeholder="Enter your malpractice insurance policy number"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #4b5563',
                        background: '#374151',
                        color: '#f9fafb',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label htmlFor="cred-w9" style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: '#f9fafb'
                    }}>
                      W-9 Form *
                    </label>
                    <input
                      id="cred-w9"
                      type="file"
                      name="w9"
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx"
                      required
                      aria-required="true"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #4b5563',
                        background: '#374151',
                        color: '#f9fafb',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Medicaid Information Section */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{
                  fontSize: '1.5rem',
                  color: '#D4A017',
                  marginBottom: '1.5rem',
                  borderBottom: '2px solid #D4A017',
                  paddingBottom: '0.5rem'
                }}>
                  Medicaid Information
                </h3>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontWeight: '600',
                    color: '#f9fafb',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      name="hasMedicaidNumber"
                      checked={formData.hasMedicaidNumber}
                      onChange={handleInputChange}
                      style={{
                        width: '1rem',
                        height: '1rem',
                        accentColor: '#D4A017'
                      }}
                    />
                    I already have an individual Medicaid number
                  </label>
                </div>

                {formData.hasMedicaidNumber && (
                  <div>
                    <label htmlFor="cred-medicaid" style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: '#f9fafb'
                    }}>
                      Medicaid Number
                    </label>
                    <input
                      id="cred-medicaid"
                      type="text"
                      name="medicaidNumber"
                      value={formData.medicaidNumber}
                      onChange={handleInputChange}
                      placeholder="Enter your Medicaid number"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #4b5563',
                        background: '#374151',
                        color: '#f9fafb',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                )}
              </div>

              {/* State and Insurance Panels Section */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{
                  fontSize: '1.5rem',
                  color: '#D4A017',
                  marginBottom: '1.5rem',
                  borderBottom: '2px solid #D4A017',
                  paddingBottom: '0.5rem'
                }}>
                  Practice Location & Insurance Panels
                </h3>

                <div style={{ marginBottom: '2rem' }}>
                  <label htmlFor="cred-state" style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#f9fafb'
                  }}>
                    What state will you be practicing in? *
                  </label>
                  <select
                    id="cred-state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    aria-required="true"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: '1px solid #4b5563',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select a state</option>
                    {states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '1rem',
                    fontWeight: '600',
                    color: '#f9fafb'
                  }}>
                    Which insurance panels would you like to consider? (Select all that apply)
                  </label>

                  {insuranceOptions.map(category => (
                    <div key={category.category} style={{ marginBottom: '1.5rem' }}>
                      <h4 style={{
                        fontSize: '1.1rem',
                        color: '#D4A017',
                        marginBottom: '0.75rem',
                        fontWeight: '600'
                      }}>
                        {category.category}
                      </h4>
                      <div style={{ display: 'grid', gap: '0.5rem', marginLeft: '1rem' }}>
                        {category.options.map(option => (
                          <label key={option} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            color: '#f9fafb',
                            cursor: 'pointer'
                          }}>
                            <input
                              type="checkbox"
                              checked={formData.insurancePanels.includes(option)}
                              onChange={() => handleInsurancePanelChange(option)}
                              style={{
                                width: '1rem',
                                height: '1rem',
                                accentColor: '#D4A017'
                              }}
                            />
                            {option}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center',
                marginTop: '2rem'
              }}>
                <Link
                  to="/"
                  className="btn btn-secondary"
                  style={{ textDecoration: 'none' }}
                >
                  Back to Home
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Submitting...' : 'Submit Credentialing Information'}
                </button>
              </div>
            </form>
          </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CredentialingPage;